import { useState, useEffect } from 'react';
import { Download, Globe, Plus } from 'lucide-react';

// iOS Safari/Chrome share icon — square with upward arrow
function IosShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v12" />
      <path d="m5 9 7-7 7 7" />
      <rect x="3" y="12" width="18" height="10" rx="2" />
    </svg>
  );
}

// iOS Chrome "..." menu icon — three horizontal dots
function ChromeMenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <circle cx="6" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="18" cy="12" r="2" />
    </svg>
  );
}

interface InstallSplashProps {
  onContinue: () => void;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function getIOSVersion(): number {
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (match) return parseInt(match[1]) + parseInt(match[2]) / 10;
  return 0;
}


export function InstallSplash({ onContinue }: InstallSplashProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [installing, setInstalling] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    // Check if the install prompt was already captured globally
    if ((window as any).deferredInstallPrompt) {
      setDeferredPrompt((window as any).deferredInstallPrompt);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const customHandler = (e: Event) => {
      setDeferredPrompt((e as CustomEvent).detail);
    };

    window.addEventListener('beforeinstallprompt', handler as any);
    window.addEventListener('appbeforeinstallprompt', customHandler as any);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as any);
      window.removeEventListener('appbeforeinstallprompt', customHandler as any);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          (window as any).deferredInstallPrompt = null;
          setDeferredPrompt(null);
          onContinue();
        }
      } catch (err) {
        console.error('PWA install error:', err);
      } finally {
        setInstalling(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0A0B0E] flex flex-col items-center justify-center p-6 z-50">
      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-sm w-full flex flex-col items-center gap-8">
        {/* Logo */}
        <div className="flex flex-col items-center gap-4">
          <div className="w-24 h-24 rounded-3xl bg-[#14161B] border border-[#2D3139] flex items-center justify-center shadow-2xl">
            <img src="/icons/icon-192.png" alt="Bip Teams" className="w-16 h-16 object-contain" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-black text-white tracking-tight">Bip Teams</h1>
            <p className="text-slate-400 text-sm mt-1">Votre espace de travail</p>
          </div>
        </div>

        {/* Install instructions */}
        <div className="w-full bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-3">
          <p className="text-white font-semibold text-center text-sm">
            📲 Installez l'application pour une meilleure expérience
          </p>

          {ios ? (
            /* iOS instructions */
            <div className="space-y-4">
              {getIOSVersion() >= 16.4 && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2 text-[10px] text-emerald-400 font-medium text-center">
                  iOS {getIOSVersion().toFixed(1)} — Notifications push disponibles
                </div>
              )}

              {/* Safari */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-md bg-blue-500/20 flex items-center justify-center">
                    <Globe className="w-3 h-3 text-blue-400" />
                  </div>
                  <span className="text-xs font-bold text-white">Via Safari (Toutes versions d'iOS)</span>
                </div>
                <div className="space-y-1.5">
                  {[
                    <>Appuyez sur <IosShareIcon className="w-4 h-4 inline text-blue-400" /> <strong className="text-white">Partager</strong> (en bas de Safari)</>,
                    <>Faites défiler et choisissez <span className="inline-flex items-center gap-0.5"><Plus className="w-3 h-3 text-indigo-400" /><strong className="text-white">Sur l'écran d'accueil</strong></span></>,
                    <>Appuyez sur <strong className="text-white">Ajouter</strong></>
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2 text-slate-400 text-[11px]">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-[10px] text-blue-400 font-bold">{i + 1}</span>
                      <span className="pt-0.5">{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chrome iOS 16.4+ */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-md bg-green-500/20 flex items-center justify-center">
                    <ChromeMenuIcon className="w-3 h-3 text-green-400" />
                  </div>
                  <span className="text-xs font-bold text-white">Via Google Chrome (iOS 16.4 minimum)</span>
                </div>
                <div className="space-y-1.5">
                  {[
                    <>Appuyez sur <IosShareIcon className="w-4 h-4 inline text-blue-400" /> <strong className="text-white">Partager</strong> (en haut à droite dans la barre d'adresse)</>,
                    <>Sélectionnez <span className="inline-flex items-center gap-0.5"><Plus className="w-3 h-3 text-indigo-400" /><strong className="text-white">Ajouter à l'écran d'accueil</strong></span></>,
                    <>Appuyez sur <strong className="text-white">Ajouter</strong></>
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2 text-slate-400 text-[11px]">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/15 border border-green-500/25 flex items-center justify-center text-[10px] text-green-400 font-bold">{i + 1}</span>
                      <span className="pt-0.5">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Android / Chrome */
            <p className="text-slate-400 text-xs text-center leading-relaxed">
              Ajoutez l'app sur votre écran d'accueil pour un accès rapide et les notifications push.
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="w-full space-y-3">
          {!ios && (
            <button
              onClick={handleInstall}
              disabled={!deferredPrompt || installing}
              className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-bold text-sm transition-all ${
                deferredPrompt
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 active:scale-95'
                  : 'bg-indigo-600/30 text-indigo-400 cursor-not-allowed'
              }`}
            >
              <Download className="w-4 h-4" />
              {installing ? 'Installation...' : deferredPrompt ? 'Installer l\'application' : 'Installation disponible dans Chrome'}
            </button>
          )}

          <button
            onClick={onContinue}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-semibold text-sm bg-[#14161B] border border-[#2D3139] text-slate-300 hover:text-white hover:border-[#3D4149] transition-all active:scale-95"
          >
            <Globe className="w-4 h-4 text-slate-500" />
            Continuer dans le navigateur
          </button>
        </div>

        <p className="text-slate-600 text-xs text-center">
          Vous pouvez toujours installer l'app plus tard depuis votre navigateur
        </p>
      </div>
    </div>
  );
}
